import { useRef, useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Sparkles, FileText, Loader2, X, CheckCircle2, Paperclip } from "lucide-react";

const MAX_BYTES = 20 * 1024 * 1024;

const ACCEPT = ".pdf,.jpg,.jpeg,.png,.doc,.docx";

// Mirrors ALLOWED_CONTENT_TYPES in backend/routes/order.py. Checked here for a
// fast, friendly failure; the backend re-checks and is the real gate.
const ALLOWED_TYPES = [
  "application/pdf",
  "image/jpeg",
  "image/jpg",
  "image/png",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];

const EXT_FALLBACK = /\.(pdf|jpe?g|png|docx?)$/i;

const prettySize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * Auto-fill helper for the B2B Create Order form.
 *
 * Presentational only: the parent page owns the document, the extraction result
 * and the invoice attachment, matching how these pages already manage state.
 *
 * IMPORTANT: uploading here never makes the document the order's invoice. That
 * only happens when the user clicks "Attach as Invoice", which calls
 * onAttachAsInvoice with the ORIGINAL File.
 *
 * Props:
 *   document          File|null   the currently selected document
 *   onDocumentChange  (File|null) => void
 *   onExtracted       (extracted) => void   called with normalized values
 *   onAttachAsInvoice (File) => void        explicit user action only
 *   attachedAsInvoice boolean
 *   onDetachInvoice   () => void
 *   disabled          boolean
 */
const AiDocumentExtract = ({
  document: file,
  onDocumentChange,
  onExtracted,
  onAttachAsInvoice,
  attachedAsInvoice = false,
  onDetachInvoice,
  disabled = false,
}) => {
  const [extracting, setExtracting] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const acceptFile = (picked) => {
    if (!picked) return;

    if (picked.size === 0) {
      toast.error("That file is empty.");
      return;
    }
    if (picked.size > MAX_BYTES) {
      toast.error("File size must be less than 20MB");
      return;
    }
    const typeOk =
      ALLOWED_TYPES.includes(picked.type) || EXT_FALLBACK.test(picked.name || "");
    if (!typeOk) {
      toast.error("Unsupported file type. Use PDF, JPEG, PNG, DOC or DOCX.");
      return;
    }

    onDocumentChange(picked);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (disabled || extracting) return;
    acceptFile(e.dataTransfer?.files?.[0]);
  };

  const handleRemove = () => {
    onDocumentChange(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  const handleExtract = async () => {
    if (!file || extracting) return; // guards duplicate requests

    setExtracting(true);
    try {
      const payload = new FormData();
      payload.append("document", file);

      const res = await axios.post(`${API}/orders/extract-document`, payload, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const { extracted, found_any: foundAny, unsupported_reason: unsupported } =
        res.data || {};

      if (unsupported) {
        toast.info(unsupported);
        return;
      }
      if (!foundAny) {
        toast.info(
          "Couldn't find matching order details in this document. You can enter the information manually."
        );
        return;
      }

      // Development diagnostic; present only when the backend has
      // DOCUMENT_EXTRACTION_DEBUG enabled. Undefined in normal operation.
      onExtracted(extracted, res.data?.debug);
    } catch (err) {
      // Extraction is an assist, never a blocker — the form still works.
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "Couldn't read the document. You can enter the information manually."
      );
    } finally {
      setExtracting(false);
    }
  };

  return (
    <div className="rounded-lg border border-teal-200 bg-teal-50/50 p-4 space-y-3">
      <div className="flex items-start gap-2">
        <Sparkles className="h-4 w-4 mt-0.5 shrink-0" style={{ color: "#00A699" }} />
        <div>
          <p className="text-sm font-medium text-slate-900">Auto-fill Order Details</p>
          <p className="text-xs text-slate-600">
            Upload a document and we'll find information that matches the order
            fields below. Nothing is submitted — you can review and edit everything.
          </p>
        </div>
      </div>

      {!file ? (
        <div
          role="button"
          tabIndex={0}
          onClick={() => !disabled && inputRef.current?.click()}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              if (!disabled) inputRef.current?.click();
            }
          }}
          onDragOver={(e) => {
            e.preventDefault();
            if (!disabled) setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          className={`flex flex-col items-center justify-center gap-1 rounded-md border-2 border-dashed px-4 py-6 text-center transition-colors ${
            disabled
              ? "cursor-not-allowed border-slate-200 opacity-60"
              : dragOver
              ? "cursor-pointer border-teal-400 bg-teal-50"
              : "cursor-pointer border-slate-300 bg-white hover:border-teal-300"
          }`}
        >
          <FileText className="h-5 w-5 text-slate-400" />
          <p className="text-sm text-slate-700">
            Drop your document here or <span className="font-medium" style={{ color: "#00A699" }}>browse</span>
          </p>
          <p className="text-xs text-slate-500">PDF, JPEG, PNG, DOC, DOCX • Max 20MB</p>
        </div>
      ) : (
        <div className="rounded-md border border-slate-200 bg-white px-3 py-2">
          <div className="flex items-center gap-2">
            <FileText className="h-4 w-4 shrink-0 text-slate-400" />
            <span className="truncate text-sm text-slate-800" title={file.name}>
              {file.name}
            </span>
            <span className="shrink-0 text-xs text-slate-500">
              {prettySize(file.size)}
            </span>
            <button
              type="button"
              onClick={handleRemove}
              disabled={extracting}
              className="ml-auto shrink-0 rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600 disabled:opacity-50"
              aria-label="Remove document"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {attachedAsInvoice && (
            <div className="mt-2 flex items-center gap-1.5 text-xs" style={{ color: "#00A699" }}>
              <CheckCircle2 className="h-3.5 w-3.5" />
              <span>Attached as Invoice Document</span>
              {onDetachInvoice && (
                <button
                  type="button"
                  onClick={onDetachInvoice}
                  className="ml-1 underline hover:no-underline"
                >
                  Undo
                </button>
              )}
            </div>
          )}
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        className="hidden"
        onChange={(e) => {
          acceptFile(e.target.files?.[0]);
          e.target.value = ""; // allow re-picking the same filename
        }}
      />

      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          size="sm"
          onClick={handleExtract}
          disabled={!file || extracting || disabled}
          style={{ backgroundColor: "#00A699" }}
        >
          {extracting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Reading document and extracting order details...
            </>
          ) : (
            <>
              <Sparkles className="mr-2 h-4 w-4" />
              Extract Details
            </>
          )}
        </Button>

        {file && !attachedAsInvoice && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => onAttachAsInvoice(file)}
            disabled={extracting || disabled}
          >
            <Paperclip className="mr-2 h-4 w-4" />
            Attach as Invoice
          </Button>
        )}
      </div>
    </div>
  );
};

export default AiDocumentExtract;
